import { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Camera, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PRESETS = ["#E05A47", "#5F8A6F", "#EAA144", "#4A4E7A", "#C94937", "#8C6BAF"];

export default function ProfileDialog({ open, onOpenChange }) {
  const { user, setUser } = useAuth();
  const fileRef = useRef();
  const [name, setName] = useState(user?.name || "");
  const [picture, setPicture] = useState(user?.picture || null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const upload = async (files) => {
    const f = files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPicture(fileUrl(res.data.path));
    } catch { toast.error("Échec de l'envoi de la photo"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.patch("/auth/profile", { name, picture });
      setUser(res.data);
      toast.success("Profil mis à jour");
      onOpenChange(false);
    } catch { toast.error("Impossible d'enregistrer"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl" data-testid="profile-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Mon profil</DialogTitle>
          <DialogDescription className="sr-only">Modifier son prénom et son avatar</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center">
          <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="avatar-input" onChange={(e) => upload(e.target.files)} />
          <button onClick={() => fileRef.current?.click()} data-testid="avatar-upload-btn"
            className="relative flex h-24 w-24 items-center justify-center overflow-hidden rounded-full text-3xl font-bold text-white at-press at-shadow-lg"
            style={{ background: picture ? "var(--at-elevated)" : PRESETS[0] }}>
            {picture && /^(https?:|\/)/.test(picture) ? <img src={picture} alt="" className="h-full w-full object-cover" /> : (name?.[0]?.toUpperCase() || "?")}
            <span className="absolute bottom-0 right-0 flex h-8 w-8 items-center justify-center rounded-full border-2 border-white text-white" style={{ background: "var(--at-brand)" }}>
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
            </span>
          </button>

          {/* Avatars couleur si pas de photo */}
          <div className="mt-3 flex gap-2">
            {PRESETS.map((c) => (
              <button key={c} data-testid={`preset-${c}`} onClick={() => setPicture(null)}
                className="h-7 w-7 rounded-full at-press" style={{ background: c, outline: !picture ? "2px solid var(--at-text)" : "none", outlineOffset: 2 }} />
            ))}
          </div>
        </div>

        <div className="mt-3 space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--at-muted)" }}>Prénom affiché</Label>
          <Input data-testid="profile-name-input" value={name} onChange={(e) => setName(e.target.value)} className="h-12 rounded-2xl border-black/10 bg-white" placeholder="Votre prénom" />
        </div>

        <Button data-testid="save-profile-btn" onClick={save} disabled={saving}
          className="mt-2 h-12 w-full rounded-2xl font-semibold text-white at-press" style={{ background: "var(--at-brand)" }}>
          {saving ? "Enregistrement..." : "Enregistrer"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
